/**
 * Template Actions API
 *
 * Save/unsave templates for a namespace, fetch saved IDs, and record usage.
 */
import { get, post, del } from '../core';

/**
 * Saves a template for the current namespace
 * @param {string} templateId - The ID of the template to save
 * @param {string} namespaceId - The ID of the namespace to save the template in
 * @returns {Promise<Object>} - Promise resolving to success message
 */
export async function saveTemplate(templateId, namespaceId) {
  if (!namespaceId) {
    throw new Error('Namespace ID is required to save templates');
  }
  
  return post(`/templates/save/${templateId}?namespaceId=${encodeURIComponent(namespaceId)}`, {});
}

/**
 * Removes a template from the current namespace's saved templates
 * @param {string} templateId - The ID of the template to unsave
 * @param {string} namespaceId - The ID of the namespace to remove the template from
 * @returns {Promise<Object>} - Promise resolving to success message
 */
export async function unsaveTemplate(templateId, namespaceId) {
  if (!namespaceId) {
    throw new Error('Namespace ID is required to unsave templates');
  }
  
  return del(`/templates/save/${templateId}?namespaceId=${encodeURIComponent(namespaceId)}`);
}

/**
 * Fetches the list of template IDs saved in the current namespace
 * @param {string} namespaceId - The ID of the namespace to fetch saved templates from
 * @returns {Promise<Array<string>>} - Promise resolving to an array of saved template IDs
 */
export async function getSavedTemplateIds(namespaceId) {
  if (!namespaceId) {
    console.warn('No namespace ID provided, cannot fetch saved templates');
    return [];
  }
  
  try {
    // Use the namespaces endpoint to get namespace details including savedTemplates
    const namespaceData = await get(`/namespaces/${namespaceId}`);
    
    // Extract savedTemplates from the namespace data
    if (namespaceData && Array.isArray(namespaceData.savedTemplates)) {
      return namespaceData.savedTemplates;
    }
    
    // In case the savedTemplates array doesn't exist
    return [];
  } catch (error) {
    console.error('Error fetching saved template IDs:', error);
    return []; // Return empty array on error to avoid breaking the UI
  }
}

/**
 * Record template usage when a user clicks the Canva link
 * @param {string} templateId - The ID of the template being used
 * @returns {Promise<Object>} - The success response
 */
export async function useTemplate(templateId) {
  try {
    return await post(`/templates/use/${templateId}`, {});
  } catch (error) {
    console.error('Error recording template usage:', error);
    // Don't throw the error - this is a non-critical operation
    return null;
  }
}

export default {
  saveTemplate,
  unsaveTemplate,
  getSavedTemplateIds,
  useTemplate
}; 